import {
  OPERATIONAL_TOLERANCE_COUNTER_TO_OPTION,
  withBidderCounter,
} from '../../../shared/offer-domain/counter';
import {
  formatParcelSize,
  formatPortDateRange,
  formatPortLocation,
} from '../../TenderEvaluation/utils/deliveryDetailUtils';

const filled = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return null;
};

const listPort = (ports, index) => {
  if (!Array.isArray(ports) || !ports.length) return null;
  const match = ports.find(port => Number(port?.index) === index);
  return match || ports[index - 1] || null;
};

export const parcelUnit = procurement =>
  String(procurement?.productName || '')
    .toUpperCase()
    .includes('LPG')
    ? 'MT'
    : 'MB';

export const offerToleranceProcurement = (
  procurement,
  partner,
  requirement
) => {
  const spec = partner?.bidSpec || {};
  return withBidderCounter(
    {
      ...(procurement || {}),
      operationalTolerance: filled(
        spec.operationalTolerance,
        partner?.operationalTolerance,
        requirement?.operationalTolerance,
        procurement?.operationalTolerance
      ),
      partyType: filled(
        spec.operationalToleranceOptionLabel,
        spec.partyType,
        partner?.operationalToleranceOptionLabel,
        partner?.partyType,
        requirement?.partyType,
        procurement?.partyType
      ),
    },
    { ...partner, ...spec }
  );
};

const optionLabel = value => {
  if (value == null || value === '') return null;
  const mapped =
    OPERATIONAL_TOLERANCE_COUNTER_TO_OPTION[value] ??
    OPERATIONAL_TOLERANCE_COUNTER_TO_OPTION[Number(value)];
  if (mapped) return mapped;
  const text = String(value).trim();
  return text || null;
};

// Requirement option is the procurement partyType (Buyer Option / Seller Option).
// Counter fields from the mitra offer are not used here.
export const requirementToleranceProcurement = (requirement, procurement) => ({
  operationalTolerance: filled(
    requirement?.operationalTolerance,
    requirement?.OperationalTolerance,
    procurement?.operationalTolerance,
    procurement?.OperationalTolerance
  ),
  partyType: optionLabel(
    filled(
      requirement?.partyType,
      requirement?.PartyType,
      procurement?.partyType,
      procurement?.PartyType
    )
  ),
});

export const bidDetail = (partner, line) => ({
  ...(partner?.bidSpec || {}),
  ...(line || {}),
  ...(line?.detail || {}),
});

export const isCounterBid = partner => partner?.kind === 'counter';

const listedField = (listed, ...keys) => {
  if (!listed || typeof listed !== 'object') return null;
  return filled(...keys.map(key => listed[key]));
};

const portParcel = (detail, index, counter) => {
  const listed = listPort(detail.ports, index);
  const own = filled(
    detail[`dischargeQty${index}`],
    detail[`DischargeQty${index}`],
    index === 1 ? detail.parcelSize : detail[`parcelSize${index}`],
    index === 1 ? detail.ParcelSize : detail[`ParcelSize${index}`],
    listedField(
      listed,
      'dischargeQty',
      'parcelSize',
      'parcel',
      'quantity',
      'qty'
    )
  );
  if (!counter) return own;
  const countered =
    index === 1
      ? filled(detail.counterParcelSize, detail.counterDeliveryBasis)
      : filled(detail[`counterParcelSize${index}`]);
  return filled(countered, own);
};

const portName = (detail, index, counter) => {
  const listed = listPort(detail.ports, index);
  const listedName =
    typeof listed === 'string'
      ? listed
      : listedField(listed, 'name', 'port', 'incotermLocation');
  const own = filled(
    detail[`port${index}`],
    detail[`Port${index}`],
    detail[index === 1 ? 'incotermLocation' : `incotermLocation${index}`],
    index === 1 ? detail.loadingPort : null,
    listedName,
    index === 1 ? detail.portName : null
  );
  if (!counter) return own;
  return filled(
    detail[`counterPort${index}`],
    index === 1 ? detail.counterLoadingPort : null,
    own
  );
};

const portDate = (detail, index, counter) => {
  const listed = listPort(detail.ports, index);
  const timeline = detail.timeline || {};
  const timelineStart =
    timeline[index === 1 ? 'startDate' : `startDate${index}`];
  const timelineEnd = timeline[index === 1 ? 'endDate' : `endDate${index}`];
  const fromTimeline = formatPortDateRange(timelineStart, timelineEnd, null);
  if (fromTimeline && fromTimeline !== '-') return fromTimeline;

  const startKey = `port${index}StartDate`;
  const endKey = `port${index}EndDate`;
  const addKey = index === 1 ? 'addAld' : `addAld${index}`;
  const aldKey = `aldAdd${index}`;
  const counterStart =
    index === 1 ? detail.counterStartDate : detail[`counterStartDate${index}`];
  const counterEnd =
    index === 1 ? detail.counterEndDate : detail[`counterEndDate${index}`];
  const counterAdd =
    index === 1 ? detail.counterAddAld : detail[`counterAddAld${index}`];
  const quantityStart =
    index === 2 ? (detail.startDate ?? detail.StartDate) : null;
  const quantityEnd = index === 2 ? (detail.endDate ?? detail.EndDate) : null;
  const timelineType =
    index === 1
      ? filled(detail.timelineType, detail.TimelineType, listed?.timelineType)
      : null;
  return formatPortDateRange(
    counter
      ? filled(counterStart, detail[startKey], quantityStart, listed?.startDate)
      : filled(detail[startKey], quantityStart, listed?.startDate),
    counter
      ? filled(counterEnd, detail[endKey], quantityEnd, listed?.endDate)
      : filled(detail[endKey], quantityEnd, listed?.endDate),
    counter
      ? filled(
          counterAdd,
          detail[addKey],
          detail[aldKey],
          timelineType,
          listed?.addAld,
          listed?.aldAdd,
          listed?.timelineType,
          listed?.date
        )
      : filled(
          detail[addKey],
          detail[`AddAld${index === 1 ? '' : index}`],
          detail[aldKey],
          timelineType,
          listed?.addAld,
          listed?.aldAdd,
          listed?.timelineType,
          listed?.date
        )
  );
};

const firstNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const match = String(value)
      .trim()
      .match(/-?\d+(?:\.\d+)?/);
    if (match && Number.isFinite(Number(match[0]))) return Number(match[0]);
  }
  return null;
};

export const lineOwnerEstimate = line =>
  firstNumber(
    line?.ownerEstimate,
    line?.hpsAlpha,
    line?.alphaHps,
    line?.alphaDyedHps,
    line?.detail?.ownerEstimate,
    line?.detail?.hpsAlpha,
    line?.detail?.alphaHps
  );

export const lineMitraLastOffer = line =>
  firstNumber(
    line?.mitraLastOffer,
    line?.improvementAlpha,
    line?.alphaImprovement,
    line?.detail?.mitraLastOffer,
    line?.detail?.improvementAlpha,
    line?.detail?.alphaImprovement,
    line?.alpha
  );

export const buildPortSlots = (
  detail = {},
  { counter = false, unit = 'MB' } = {}
) =>
  [1, 2, 3].map(index => {
    const name = portName(detail, index, counter);
    const parcel = portParcel(detail, index, counter);
    const date = portDate(detail, index, counter);
    const hasDate = date && date !== '-';
    const names =
      index === 1 && Array.isArray(detail.fobLocationNames)
        ? detail.fobLocationNames.filter(Boolean)
        : [];
    if (name == null && parcel == null && !hasDate && !names.length)
      return null;
    return {
      index,
      name: formatPortLocation(name),
      names,
      parcel: formatParcelSize(parcel, unit),
      date: hasDate ? date : '-',
    };
  });
